import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clapperboard, Loader2, Lock, Unlock, RefreshCw, Wand2, Server, Film } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDuration } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";
import NoProject from "@/components/NoProject";

function Scene({ pid, scene, music, onChange }) {
  const [cmd, setCmd] = useState("");
  const [busy, setBusy] = useState("");
  const act = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); toast.success(msg); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const fillPct = scene.target_seconds ? Math.min(100, Math.round((scene.filled_seconds / scene.target_seconds) * 100)) : 0;

  return (
    <div className="studio-card p-4" data-testid={`scene-${scene.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-semibold text-sm flex-1">{scene.title}</span>
        <span className="tech-badge">{scene.event}</span>
        <span className="tech-badge" style={{ color: "#8B5CF6" }}>{scene.mood}</span>
        {scene.continuous && <span className="tech-badge" style={{ color: "#00F0FF" }}>continuous</span>}
      </div>
      <div className="flex items-center gap-3 mb-2 text-[12px] font-mono text-[#94A3B8]">
        <span>{scene.items.length} shots</span>
        <span>{fmtDuration(scene.filled_seconds)} / {fmtDuration(scene.target_seconds)}</span>
        <span>transition: {scene.transition}</span>
      </div>
      <div className="progress-track mb-3"><div className="progress-fill" style={{ width: `${fillPct}%` }} /></div>

      {scene.items.length > 0 && (
        <div className="flex gap-1 mb-3 overflow-x-auto">
          {scene.items.map((it, i) => (
            <div key={i} className="shrink-0 px-2 py-1 rounded bg-[#0c0e14] text-[10px] font-mono text-[#94A3B8] border border-[#252A3B]" title={it.filename}>
              {fmtDuration(it.source_out - it.source_in)}{it.slow_motion ? " ½×" : ""}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <select className="studio-input !py-1.5 text-xs max-w-[160px]" value={scene.music_media_id || ""} data-testid={`scene-music-${scene.id}`}
          onChange={(e) => act("music", () => api.setSceneMusic(pid, scene.id, e.target.value || null), "Music set")} disabled={scene.locked}>
          <option value="">No music</option>
          {music.map((m) => <option key={m.id} value={m.id}>{m.filename}</option>)}
        </select>
        <button className="btn btn-ghost !py-1.5 !px-2.5 text-xs" disabled={scene.locked || busy} onClick={() => act("regen", () => api.regenScene(pid, scene.id), "Scene regenerated")} data-testid={`scene-regen-${scene.id}`}>
          {busy === "regen" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Regenerate
        </button>
        <button className="btn btn-ghost !py-1.5 !px-2.5 text-xs" onClick={() => act("lock", () => api.lockScene(pid, scene.id, !scene.locked), scene.locked ? "Unlocked" : "Locked")} data-testid={`scene-lock-${scene.id}`}>
          {scene.locked ? <Lock size={13} className="text-[#E6B04E]" /> : <Unlock size={13} />} {scene.locked ? "Locked" : "Lock"}
        </button>
      </div>

      <div className="flex gap-2 mt-2">
        <input className="studio-input !py-1.5 text-xs" placeholder='AI command e.g. "make more cinematic" / "keep continuous"'
          value={cmd} onChange={(e) => setCmd(e.target.value)} disabled={scene.locked} data-testid={`scene-cmd-${scene.id}`} />
        <button className="btn btn-gold !py-1.5 !px-3 text-xs" disabled={scene.locked || busy || !cmd.trim()}
          onClick={() => act("cmd", async () => { await api.sceneCommand(pid, scene.id, cmd); setCmd(""); }, "Command applied")} data-testid={`scene-cmd-btn-${scene.id}`}>
          {busy === "cmd" ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />}
        </button>
      </div>
    </div>
  );
}

export default function Timeline() {
  const { projectId } = useApp();
  const navigate = useNavigate();
  const { data, refresh } = usePoll(() => (projectId ? api.getEditPlan(projectId) : Promise.resolve(null)), 6000, [projectId]);
  const { data: musicData } = usePoll(() => (projectId ? api.music(projectId) : Promise.resolve(null)), 30000, [projectId]);
  if (!projectId) return <NoProject title="Timeline needs a project" />;

  const chapters = data?.chapters || [];
  const music = musicData?.tracks || [];
  const render = async () => { try { await api.startRender(projectId); toast.success("Render started"); navigate("/render"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  return (
    <div className="p-8 fade-up space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#212534] flex items-center justify-center"><Clapperboard size={22} className="text-[#E6B04E]" /></div>
          <div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Edit Plan &amp; Timeline</h1>
            <p className="text-[#94A3B8] text-sm">Editable scenes · est {fmtDuration(data?.estimated_seconds || 0)} / target {fmtDuration(data?.target_seconds || 0)}</p>
          </div>
        </div>
        {chapters.length > 0 && <button className="btn btn-gold" onClick={render} data-testid="btn-render"><Server size={16} /> Render Film</button>}
      </div>

      {chapters.length === 0 ? (
        <div className="studio-card p-12 text-center text-[#64748B]">No edit plan. Generate a <b>Story</b> then Build Edit Plan.</div>
      ) : (
        <div className="space-y-6">
          {chapters.map((ch, ci) => (
            <div key={ch.id}>
              <div className="flex items-center gap-2 mb-2">
                <Film size={15} className="text-[#E6B04E]" />
                <h3 className="font-head font-bold">{ci + 1}. {ch.title}</h3>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {ch.scenes.map((sc) => <Scene key={sc.id} pid={projectId} scene={sc} music={music} onChange={refresh} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
